const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';

const getTranslations = () => {
    try {
        return JSON.parse(window.localStorage.getItem('app-languages') || '{}') || {};
    } catch (error) {
        return {};
    }
};

const translateParamValue = (value, translations) => {
    if (Array.isArray(value)) {
        return value.map(item => translateParamValue(item, translations));
    }

    if (isPlainObject(value)) {
        return Object.keys(value).reduce((carry, key) => {
            carry[key] = translateParamValue(value[key], translations);
            return carry;
        }, {});
    }

    if (typeof value !== 'string') {
        return value;
    }

    return Object.prototype.hasOwnProperty.call(translations, value) ? translations[value] : value;
};

const interpolate = (message, params = {}) => {
    if (typeof message !== 'string') {
        return message;
    }

    return Object.keys(params).reduce((carry, key) => {
        const value = params[key];
        const resolvedValue = value === null || value === undefined ? '' : value;
        const pattern = new RegExp(`:${key}\\b`, 'g');

        return carry.replace(pattern, resolvedValue);
    }, message);
};

const flattenErrorBag = (errors = {}) => {
    if (Array.isArray(errors)) {
        return errors
            .flatMap(item => flattenErrorBag(item))
            .filter(Boolean);
    }

    if (!isPlainObject(errors)) {
        return typeof errors === 'string' ? [errors] : [];
    }

    return Object.values(errors)
        .flatMap(value => {
            if (Array.isArray(value)) {
                return value.flatMap(item => flattenErrorBag(item));
            }

            if (isPlainObject(value)) {
                return flattenErrorBag(value);
            }

            return typeof value === 'string' ? [value] : [];
        })
        .filter(Boolean);
};

const uniqueMessages = messages => Array.from(new Set((messages || []).map(message => `${message || ''}`.trim()).filter(Boolean)));

const joinMessages = messages => uniqueMessages(messages).slice(0, 3).join(' • ');

const hasTranslation = (translations, key) => Object.prototype.hasOwnProperty.call(translations, key);

const firstNonEmptyString = (...values) => {
    for (const value of values) {
        if (typeof value === 'string' && value.trim() !== '') {
            return value.trim();
        }
    }

    return '';
};

const resolveStatusMessage = (status, translations) => {
    if (!status) {
        return '';
    }

    const normalized = `${status}`;
    return hasTranslation(translations, normalized) ? translations[normalized] : '';
};

const isGenericFailureMessage = (message, translations, fallback = {}) => {
    const normalized = `${message || ''}`.trim().toLowerCase();

    if (!normalized) {
        return true;
    }

    if (/^request failed with status code \d+$/i.test(normalized)) {
        return true;
    }

    const genericMessages = [
        translations.failed_response,
        fallback.defaultMessage,
        'something went wrong',
        'une erreur s’est produite',
        'une erreur s\'est produite',
        'failed_response',
    ]
        .map(item => `${item || ''}`.trim().toLowerCase())
        .filter(Boolean);

    return genericMessages.includes(normalized);
};

export const resolveTranslatedMessage = (payload = {}, fallback = {}) => {
    if (typeof payload === 'string') {
        const translations = getTranslations();
        return hasTranslation(translations, payload) ? translations[payload] : payload;
    }

    const translations = getTranslations();
    const messageKey = payload?.message_key || payload?.messageKey || fallback.key;
    const rawParams = payload?.message_params || payload?.messageParams || fallback.params || {};
    const messageParams = translateParamValue(rawParams, translations);

    if (messageKey && Object.prototype.hasOwnProperty.call(translations, messageKey)) {
        return interpolate(translations[messageKey], messageParams);
    }

    if (typeof payload?.message === 'string' && payload.message) {
        return payload.message;
    }

    if (fallback.key && Object.prototype.hasOwnProperty.call(translations, fallback.key)) {
        return interpolate(translations[fallback.key], translateParamValue(fallback.params || {}, translations));
    }

    if (typeof fallback.defaultMessage === 'string' && fallback.defaultMessage) {
        return interpolate(fallback.defaultMessage, fallback.params || {});
    }

    return translations.failed_response || 'Something went wrong';
};

export const resolveErrorMessage = (payload = {}, fallback = {key: 'failed_response'}) => {
    const translations = getTranslations();

    if (typeof payload === 'string') {
        return hasTranslation(translations, payload) ? translations[payload] : payload;
    }

    const axiosResponse = payload?.response || null;
    const axiosData = axiosResponse?.data;
    const normalizedData = isPlainObject(axiosData)
        ? axiosData
        : (isPlainObject(payload) && !axiosResponse ? payload : {});

    const validationMessages = uniqueMessages(flattenErrorBag(normalizedData?.errors || {}));
    const validationSummary = joinMessages(validationMessages);

    const translatedMessage = resolveTranslatedMessage(normalizedData, fallback);
    const explicitMessage = firstNonEmptyString(
        normalizedData?.message,
        normalizedData?.error,
        payload?.message
    );
    const debugMessage = firstNonEmptyString(
        normalizedData?.debug,
        normalizedData?.details,
        normalizedData?.exception
    );

    if (!axiosResponse && payload?.code === 'ECONNABORTED') {
        return hasTranslation(translations, 'request_timeout_message')
            ? translations.request_timeout_message
            : 'The request took too long to respond.';
    }

    if (!axiosResponse && (payload?.message === 'Network Error' || payload?.isAxiosError)) {
        return hasTranslation(translations, 'network_error_message')
            ? translations.network_error_message
            : 'Network error. Please check your connection and try again.';
    }

    if (!isGenericFailureMessage(explicitMessage, translations, fallback) && validationSummary) {
        return `${explicitMessage} ${validationSummary}`.trim();
    }

    if (validationSummary) {
        return validationSummary;
    }

    if (!isGenericFailureMessage(explicitMessage, translations, fallback)) {
        return explicitMessage;
    }

    if (!isGenericFailureMessage(translatedMessage, translations, fallback)) {
        return translatedMessage;
    }

    if (debugMessage) {
        return debugMessage;
    }

    const statusMessage = resolveStatusMessage(
        axiosResponse?.status || normalizedData?.status_code || normalizedData?.status,
        translations
    );

    if (statusMessage) {
        return statusMessage;
    }

    return translatedMessage || translations.failed_response || 'Something went wrong';
};

export const localizeAxiosResponseMessage = response => {
    if (!response || !isPlainObject(response.data)) {
        return response;
    }

    if (response.data.message_key || response.data.messageKey) {
        response.data.message = resolveTranslatedMessage(response.data);
    }

    return response;
};

export const localizeAxiosErrorMessage = error => {
    const resolvedMessage = resolveErrorMessage(error, {key: 'failed_response'});

    if (!error?.response) {
        error.response = {
            data: {
                message: resolvedMessage,
            },
            status: 0,
            statusText: '',
        };

        return error;
    }

    if (!isPlainObject(error.response.data)) {
        error.response.data = {
            message: resolvedMessage,
        };

        return error;
    }

    error.response.data.message = resolvedMessage;

    return error;
};
